import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Paper,
  Autocomplete
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const AddUser = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    nama_lengkap: '',
    is_active: 'aktif',
    emp_id: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newUserData, setNewUserData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeSearchValue, setEmployeeSearchValue] = useState('');
  const navigate = useNavigate();

  // Fetch employees data
  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch('https://apidashboard.eurekagroup.id/employee', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal mengambil data employee'
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle employee selection
  const handleEmployeeSelect = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        username: newValue.first_name || '',
        email: newValue.email || '',
        nama_lengkap: newValue.full_name || '',
        emp_id: newValue.emp_id || ''
      }));
    } else {
      // Clear form when no employee selected
      setFormData(prev => ({
        ...prev,
        username: '',
        email: '',
        nama_lengkap: '',
        emp_id: ''
      }));
    }
  };

  // Load employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({
      ...prev,
      password: password
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        ...SwalConfig.success,
        title: 'Berhasil!',
        text: 'Data berhasil disalin ke clipboard',
        timer: 1500
      });
    });
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setNewUserData(null);
    navigate('/sso/user');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!selectedEmployee) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Pilih employee terlebih dahulu'
      });
      return;
    }

    if (!formData.username || !formData.email || !formData.password || !formData.nama_lengkap || !formData.emp_id) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Semua field harus diisi'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Format email tidak valid'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          nama_lengkap: formData.nama_lengkap,
          is_active: formData.is_active,
          emp_id: formData.emp_id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('User created:', result);
      
      // Set the new user data and show modal
      setNewUserData({
        ...result.data,
        password: formData.password // Include the generated password
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error creating user:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal membuat user: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'aktif', label: 'Aktif' },
    { value: 'inaktif', label: 'Tidak Aktif' },
    { value: 'blokir', label: 'Diblokir' }
  ];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Tambah User SSO
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Buat user SSO baru untuk sistem
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sso/user')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Employee Search - First and Full Width */}
              <Grid item xs={12}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => option.full_name || ''}
                  value={selectedEmployee}
                  onChange={handleEmployeeSelect}
                  inputValue={employeeSearchValue}
                  onInputChange={(event, newInputValue) => {
                    setEmployeeSearchValue(newInputValue);
                  }}
                  loading={loadingEmployees}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cari Employee"
                      placeholder="Ketik nama lengkap untuk mencari employee..."
                      variant="outlined"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <React.Fragment>
                            {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.full_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {option.emp_id} • {option.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  noOptionsText="Tidak ada employee ditemukan"
                  loadingText="Memuat data employee..."
                />
                {selectedEmployee && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        setSelectedEmployee(null);
                        setEmployeeSearchValue('');
                        setFormData(prev => ({
                          ...prev,
                          username: '',
                          email: '',
                          nama_lengkap: '',
                          emp_id: ''
                        }));
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Hapus Pilihan Employee
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', px: 2 }}>
                    Data User SSO
                  </Typography>
                </Divider>
                {!selectedEmployee && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'info.50', 
                    borderRadius: 1, 
                    border: '1px solid',
                    borderColor: 'info.200',
                    mb: 2
                  }}>
                    <Typography variant="body2" sx={{ color: 'info.700', textAlign: 'center' }}>
                      💡 Pilih employee di atas untuk mengisi data secara otomatis
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Username */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="username"
                  label="Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Masukkan username"
                  disabled={!!selectedEmployee}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Masukkan email"
                  disabled={!!selectedEmployee}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              {/* Nama Lengkap */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nama_lengkap"
                  label="Nama Lengkap"
                  value={formData.nama_lengkap}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Masukkan nama lengkap"
                  disabled={!!selectedEmployee}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              {/* Employee ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="emp_id"
                  label="Employee ID"
                  value={formData.emp_id}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Masukkan Employee ID"
                  disabled={!!selectedEmployee}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="is_active"
                    value={formData.is_active}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Password akan digenerate otomatis"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={generatePassword}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Generate Password
                  </Button>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Password akan digenerate secara otomatis untuk keamanan
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/sso/user')}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {loading ? 'Menyimpan...' : 'Simpan User'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 2,
          pt: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ 
              bgcolor: 'success.main', 
              width: 56, 
              height: 56
            }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            User SSO Berhasil Dibuat
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Data user baru telah berhasil ditambahkan ke sistem
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', borderRadius: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {newUserData?.nama_lengkap}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  @{newUserData?.username}
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    Email
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {newUserData?.email}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(newUserData?.email)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    Employee ID
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {newUserData?.emp_id}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(newUserData?.emp_id)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    Status
                  </Typography>
                  <Chip 
                    label={newUserData?.is_active}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    Password
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}>
                    <Typography variant="body2" sx={{ 
                      flex: 1, 
                      fontFamily: 'monospace',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {newUserData?.password}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(newUserData?.password)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    Simpan password ini dengan aman. Password hanya ditampilkan sekali.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            fullWidth
            size="large"
            sx={{
              borderRadius: 1,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Selesai
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddUser;
